/**
 * Manually-measured per-route distances, used by the Run Kilometres screen to
 * work out payable kilometres from a trip count.
 *
 * Three figures per route, all in km:
 *  - `lengthKm`   — measured revenue length of ONE trip, terminal to terminal.
 *  - `deadOutKm`  — dead mileage, depot → terminal at the start of a shift.
 *  - `deadInKm`   — dead mileage, terminal → depot at the end of a shift.
 *
 * Revenue km scale with the trip count; dead mileage is incurred ONCE PER BUS
 * PER DAY (a bus deadheads out in the morning and back at night regardless of
 * how many trips it turns in between), which is why the two are counted
 * separately rather than folded into a single per-trip figure.
 *
 * Persisted to localStorage rather than the agency `/settings` store because
 * the schema-driven settings API needs a server-side schema entry per key.
 * When `occ.route_km.*` keys exist server-side, swap the two accessors below
 * and nothing else has to change.
 */

const STORAGE_KEY = "occ.mileage.route_config.v2";
/** Fired on write so open pages re-read without a reload. */
const CHANGE_EVENT = "occ:route-config-changed";

export interface RouteConfig {
  /** Measured revenue length of one trip, in km. */
  lengthKm: number;
  /** Dead mileage depot → terminal, in km. Once per bus per day. */
  deadOutKm: number;
  /** Dead mileage terminal → depot, in km. Once per bus per day. */
  deadInKm: number;
}

export type RouteConfigMap = Record<string, RouteConfig>;

/** Seeded from the CDA Orange Line so the screen isn't empty on first load. */
export const DEFAULT_ROUTE_CONFIG: RouteConfigMap = {
  "Faiz Ahmad Faiz to Airport": { lengthKm: 25.3, deadOutKm: 3.4, deadInKm: 3.4 },
  "Airport to Faiz Ahmad Faiz": { lengthKm: 25.3, deadOutKm: 3.4, deadInKm: 3.4 },
};

function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0 ? v : fallback;
}

/**
 * Normalise one stored entry. Accepts the v1 shape (a bare number meaning
 * route length) so lengths configured before dead mileage existed survive.
 */
function coerce(raw: unknown): RouteConfig | null {
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw > 0
      ? { lengthKm: raw, deadOutKm: 0, deadInKm: 0 }
      : null;
  }
  if (raw && typeof raw === "object") {
    const o = raw as Partial<RouteConfig>;
    const lengthKm = num(o.lengthKm);
    if (lengthKm <= 0) return null;
    return { lengthKm, deadOutKm: num(o.deadOutKm), deadInKm: num(o.deadInKm) };
  }
  return null;
}

export function readRouteConfig(): RouteConfigMap {
  try {
    // Fall back to the v1 key so an operator who set lengths before this
    // change doesn't lose them.
    const raw =
      localStorage.getItem(STORAGE_KEY) ??
      localStorage.getItem("occ.mileage.route_lengths.v1");
    if (!raw) return { ...DEFAULT_ROUTE_CONFIG };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const clean: RouteConfigMap = {};
    for (const [k, v] of Object.entries(parsed)) {
      const c = coerce(v);
      if (c) clean[k] = c;
    }
    return Object.keys(clean).length ? clean : { ...DEFAULT_ROUTE_CONFIG };
  } catch {
    return { ...DEFAULT_ROUTE_CONFIG };
  }
}

export function writeRouteConfig(next: RouteConfigMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  } catch {
    // Private-mode / quota failure — the in-memory value still applies for
    // this session.
  }
}

export { CHANGE_EVENT as ROUTE_CONFIG_CHANGE_EVENT, STORAGE_KEY as ROUTE_CONFIG_STORAGE_KEY };
